import { useState, useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { startOfDay } from 'date-fns'
import { setRoutingServiceDate, setRoutingDateRange } from 'slices/customer/customerDetailsSlice'
import { calculateRangePreset } from './dateRangeUtils'

/**
 * Custom hook to manage date range presets for the calendar
 * Handles preset selection, manual date range changes, and Redux state updates
 *
 * @param {Date} serviceDate - The current service date for reset functionality
 * @returns {Object} - Object containing state and handlers for date range management
 */
export const useDateRangePresets = serviceDate => {
  const dispatch = useDispatch()
  const [selectedRange, setSelectedRange] = useState([null, null])
  const [activePreset, setActivePreset] = useState(null)

  // Memoize today to avoid recreating on every render
  const today = useMemo(() => new Date(), [])

  /**
   * Apply a preset date range
   * @param {string} presetName - The name of the preset to apply
   */
  const applyPreset = useCallback(
    presetName => {
      const range = calculateRangePreset(presetName, today, serviceDate)
      if (!range) return

      setSelectedRange([range.start, range.end])
      setActivePreset(presetName)
      dispatch(setRoutingServiceDate(range.start))
      dispatch(setRoutingDateRange({ startDate: range.start, endDate: range.end }))
    },
    [dispatch, today, serviceDate]
  )

  /**
   * Handle manual date range change from calendar
   * @param {Array} newDateRange - Array of [startDate, endDate]
   */
  const handleDateChange = useCallback(
    newDateRange => {
      setSelectedRange(newDateRange)
      setActivePreset(null)

      if (newDateRange && newDateRange[0]) {
        dispatch(setRoutingServiceDate(newDateRange[0]))
        dispatch(setRoutingDateRange({ startDate: newDateRange[0], endDate: newDateRange[1] || newDateRange[0] }))
      } else {
        // Clear date range filter
        dispatch(setRoutingDateRange({ startDate: null, endDate: null }))
      }
    },
    [dispatch]
  )

  /**
   * Handle day click in the calendar for range selection
   * @param {Date} dayObj - The clicked day
   */
  const handleDayClick = useCallback(
    dayObj => {
      const rangeStart = selectedRange[0]
      const rangeEnd = selectedRange[1]

      if (!rangeStart || (rangeStart && rangeEnd)) {
        // Start a new range
        setSelectedRange([dayObj, null])
        setActivePreset(null)
      } else if (rangeStart && !rangeEnd) {
        // Complete the range
        if (dayObj < rangeStart) {
          setSelectedRange([dayObj, rangeStart])
          dispatch(setRoutingDateRange({ startDate: dayObj, endDate: rangeStart }))
        } else {
          setSelectedRange([rangeStart, dayObj])
          dispatch(setRoutingDateRange({ startDate: rangeStart, endDate: dayObj }))
        }
        setActivePreset(null)
      }

      dispatch(setRoutingServiceDate(dayObj))
    },
    [dispatch, selectedRange]
  )

  /**
   * Clear the current selection
   */
  const clearSelection = useCallback(() => {
    setSelectedRange([null, null])
    setActivePreset(null)
    dispatch(setRoutingDateRange({ startDate: null, endDate: null }))
  }, [dispatch])

  /**
   * Set a specific date range programmatically
   * @param {Date} startDate - Range start date
   * @param {Date} endDate - Range end date
   */
  const setRange = useCallback(
    (startDate, endDate) => {
      const normalizedStart = startDate ? startOfDay(startDate) : null
      const normalizedEnd = endDate ? startOfDay(endDate) : null
      setSelectedRange([normalizedStart, normalizedEnd])
      setActivePreset(null)

      if (normalizedStart) {
        dispatch(setRoutingServiceDate(normalizedStart))
        dispatch(setRoutingDateRange({ startDate: normalizedStart, endDate: normalizedEnd || normalizedStart }))
      }
    },
    [dispatch]
  )

  return {
    selectedRange,
    activePreset,
    applyPreset,
    handleDateChange,
    handleDayClick,
    clearSelection,
    setRange,
  }
}
